import { useQuery, UseQueryResult } from 'react-query';

import { useAuth } from 'src/hooks/useAuth';

import { downloadTemplate } from 'src/utils/urls';
import { showErrorToast, showSuccessToast } from 'src/utils/ToastNotification';
import { createCsvFile } from '../utils/createCsvFile';

type TResult = string;

type TError = Error;

type TVariables = {
  token: string;
  tokenType: string;
};

async function fetchTemplate({
  token,
  tokenType,
}: TVariables): Promise<TResult> {
  const response = await fetch(downloadTemplate, {
    method: 'GET',
    headers: {
      Authorization: `${tokenType} ${token}`,
    },
  });

  if (response.redirected) {
    throw new Error('Session expired');
  }

  if (!response.ok) {
    let errorMessage = 'Failed to download template';

    try {
      const text = await response.text();

      if (text) {
        const errorResponse = JSON.parse(text);

        errorMessage =
          errorResponse?.Errors?.map(
            (err: { Message: string }) => err.Message,
          ).join(', ') || errorMessage;
      }
    } catch (e) {
      console.error('Error parsing template error response:', e);
    }

    throw new Error(errorMessage);
  }

  return await response.text();
}

function useDownloadTemplate(): UseQueryResult<TResult, TError> {
  const { token, tokenType } = useAuth();

  return useQuery(
    ['get_download_template'],
    async () => {
      const csvContent = await fetchTemplate({
        token,
        tokenType,
      });

      createCsvFile(csvContent, 'UserTemplate.csv');

      showSuccessToast({
        message: 'Template downloaded successfully.',
      });

      return csvContent;
    },
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: false,

      onError: (error: Error) => {
        showErrorToast({
          message: error.message || 'Failed to download template',
        });
      },
    },
  );
}

export { useDownloadTemplate };
